import { BaseGameController } from '@/components/game/implementations/_base/_domain/controller/game-controller';
import { injectable, inject } from 'inversify';
import { IServerServiceFactory } from '@/app-modules/server-service';
import { IGame, GameCallboard, WordsearchGameCallBoard, INextGameInfo, IGameConfiguration, GameIdentity, IGameStageWinnerSet, IGameRepresentation, GameState, IGamePrizeTicketBook, BoxNumberCallboard } from '@/components/game/_domain/models';
import { WinnerCatchupData } from '@/components/game/_domain/server/winner-catchup/winner-catchup-data';
import { PrizeWinnerData } from '@/components/game/_domain/server/prize-winner/prize-winner-data';
import { SetStageData } from '@/components/game/_domain/server/set-stage';
import { getTicketBookNameForTicketBookId, Bingo60TicketBook, getTicketBookSequence } from '@/components/game/implementations/uk/bingo-60/_domain/models';
import { IWinnersTrackerService, ITrackedWinner } from '@/components/game/_domain/modules/winners-tracker-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ConfigurationPaths, IConfigurationService } from '@/app-modules/config';
import { IErrorService } from '@/app-modules/error';
import { NextGameData, CatchUpData, ResetData, CallsData, WinnersData } from '@/components/game/_domain';

// tslint:disable-next-line:interface-name
interface GameStageWinnerSetsObject {
  [key: string]: IGameStageWinnerSet;
}

interface IWordSearchGameRepresentation extends IGameRepresentation {
  nextGame?: IWordsearchNextGameInfo;
}

interface IWordsearchNextGameInfo extends INextGameInfo {
  theme?: number;
  subtheme?: string;
}

class WordsearchGame implements IGame {
  public constructor(public readonly gameIdentity: GameIdentity, public readonly gameConfiguration: IGameConfiguration) {
    this.connectionDebugInfo = 'Disconnected';
    this.state = GameState.InPlay;
  }

  public connectionDebugInfo: string;
  public state: GameState;
  public representation?: IWordSearchGameRepresentation;

  public updateRepresentation(newGameRepresentation: Partial<IWordSearchGameRepresentation>): void {
    this.representation = Object.assign({}, this.representation, newGameRepresentation || {});
  }
}


@injectable()
class WordSearchGameController extends BaseGameController {
  protected game: WordsearchGame;

  public constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IServerServiceFactory) serverServiceFactory: IServerServiceFactory,
    @inject(SERVICE_IDENTIFIERS.IWinnersTrackerService) winnersTrackerService: IWinnersTrackerService,
    @inject(SERVICE_IDENTIFIERS.IErrorService) errorService: IErrorService
  ) {
    super(configurationService, serverServiceFactory, winnersTrackerService, errorService);
  }

  public initialise(gameIdentity: GameIdentity) {
    this.game = this.getInitialGame(gameIdentity);
    this.initialiseGameServerConnection();
    this.initialiseGameRetryPolicySettings();
    this.initialiseGameServerService();
  }

  protected getInitialGame(gameIdentity: GameIdentity): WordsearchGame {
    const gameConfig = this.getGameConfiguration(gameIdentity);
    const maxNumberOfCallsPerGame = gameConfig.maxNumberOfCallsPerGame;
    const game = new WordsearchGame(gameIdentity, gameConfig);
    game.representation = {
      callboard: new GameCallboard(maxNumberOfCallsPerGame),
      wordSearchCallBoard: new WordsearchGameCallBoard(maxNumberOfCallsPerGame),
      boxNumberCallboard: new BoxNumberCallboard(),
    };
    return game;
  }

  protected subscribeToGameEvents(): void {
    this.serverSubscriptionStore.push(
      this.gameServerService.subscribeCatchUp(this.handleCatchUpMessage.bind(this)),
      this.gameServerService.subscribeReset(this.handleResetMessage.bind(this)),
      this.gameServerService.subscribeCalls(this.handleCallsMessage.bind(this)),
      this.gameServerService.subscribeNextGame(this.handleNextGameMessage.bind(this)),
      this.gameServerService.subscribePrizeWinner(this.handlePrizeWinnerMessage.bind(this)),
      this.gameServerService.subscribeWinners(this.handleWinnersMessage.bind(this)),
      this.gameServerService.subscribeSetStage(this.handleSetStageMessage.bind(this)),
    );
  }


  protected handleSetStageMessage(payload: SetStageData): void {
    const displayWinnersForInMilliseconds = 20000;

    if (payload.newStage === 0 && this.game.representation.winners && this.game.representation.winners.length > 0) {
      this.notifyGameWon();
      this.changeGameState(GameState.WinnersDisplayed);

      setTimeout(() => {
        this.changeGameState(GameState.Purchasing);
      }, displayWinnersForInMilliseconds);
    }
  }

  protected handleNextGameMessage(payload: NextGameData): void {
    this.game.updateRepresentation({
      nextGame: {
        countDownToNextGameInSeconds: payload.time,
        isLinkedGame: payload.isLinkedGame,
        prizeStructure: payload.prizeStructure,
      }
    });

    this.notifyNextGameRepresentationChanged();
  }

  private getWords(subtheme: string): string[] {
    const con = this.configurationService.getConfigurationSync();
    const words = con.getConfigurationByPath('games/wordsearch/themes/' + subtheme.toLowerCase());
    if (words) {
      return words.split(',');
    }

    return [];
  }

  protected handleCatchUpMessage(payload: CatchUpData): void {
    const words = this.getWords(payload.subtheme);
    this.game.representation.wordSearchCallBoard.setCallWords(words);
    this.game.representation.wordSearchCallBoard.catchup(payload.callValues);
    this.changeGameRepresentation({
      gameId: payload.gameNo,
      numberOfPlayers: payload.numberOfPlayers,
      isLinkedGame: payload.isLinkedGame,
      prizeStructure: payload.prizeStructure,
      wordSearchSubTheme: payload.subtheme
    });
  }

  protected handleResetMessage(payload: ResetData): void {
    const words = this.getWords(payload.subtheme);
    this.game.representation.wordSearchCallBoard.reset();
    this.game.representation.wordSearchCallBoard.setCallWords(words);
    this.changeGameState(GameState.InPlay);
    this.changeGameRepresentation({
      gameId: payload.gameId,
      numberOfPlayers: payload.numberOfPlayers,
      promoType: payload.promoType,
      promoMultiplier: payload.promoMultiplier,
      nextGame: null,
      isJackpotWon: null,
      prizeStructure: payload.prizeStructure,
      winners: null,
      wordSearchSubTheme: payload.subtheme
    });
    this.notifyPlaySoundRequest();
  }

  protected handleCallsMessage(payload: CallsData): void {
    this.game.representation.wordSearchCallBoard.update([payload.callValue], [payload.callWord], payload.callNumber);
    this.notifyGameRepresentationChanged();
  }

  protected handlePrizeWinnerMessage(payload: PrizeWinnerData): void {
    const winners = this.game.representation.winners || [];
    const payloadWinners = this.getGameStageWinnerSetsFromPrizeWinnerData(payload);

    this.changeGameRepresentation({
      winners: this.mergeGameStageWinnerSets(winners, payloadWinners),
      isJackpotWon: payload.jackpot
    });
    this.notifyGameWon();
    this.trackPrizeWinners(payloadWinners);
  }

  protected changeGameRepresentation(newGameRepresentation: Partial<IWordSearchGameRepresentation>): void {
    this.game.updateRepresentation(newGameRepresentation);
    this.notifyGameRepresentationChanged();
  }

  protected getGameStageWinnerSetsFromPrizeWinnerData(payload: PrizeWinnerData): IGameStageWinnerSet[] {
    const stageWinnerSets: GameStageWinnerSetsObject = {};
    payload.winners.forEach((winner) => {
      const gameStageWinnerSet = this.getGameStageWinnerSetForPrizeName(stageWinnerSets, winner.prizeName, winner.stage);
      const gamePrizeTicketBook = this.getGamePrizeTicketBook(gameStageWinnerSet, winner.level);
      gamePrizeTicketBook.winners.push({
        username: winner.username,
        prizeInPence: winner.prizeInPence
      });
    });

    return Object.keys(stageWinnerSets).map((prizeName) => stageWinnerSets[prizeName]);
  }


  private getGameStageWinnerSetForPrizeName(stageWinnerSets: GameStageWinnerSetsObject, winnerPrizeName: string, gameStage: number): IGameStageWinnerSet {
    let gameStageWinnerSet = stageWinnerSets[winnerPrizeName];
    if (!gameStageWinnerSet) {
      gameStageWinnerSet = {
        stage: gameStage,
        prizeName: winnerPrizeName,
        ticketBooks: []
      };
      stageWinnerSets[winnerPrizeName] = gameStageWinnerSet;
    }
    return gameStageWinnerSet;
  }

  private getGamePrizeTicketBook(gameStageWinnerSet: IGameStageWinnerSet, ticketBookLevel: number): IGamePrizeTicketBook {
    let gamePrizeTicketBook = gameStageWinnerSet.ticketBooks.find((ticketBook) => ticketBook.level === ticketBookLevel);
    if (!gamePrizeTicketBook) {
      const ticketBookId = this.getTicketBookIdForPrizeLevel(ticketBookLevel);
      gamePrizeTicketBook = {
        id: ticketBookId,
        level: ticketBookLevel,
        name: getTicketBookNameForTicketBookId(ticketBookId),
        winners: []
      };
      gameStageWinnerSet.ticketBooks.push(gamePrizeTicketBook);
    }
    return gamePrizeTicketBook;
  }

  private getTicketBookIdForPrizeLevel(prizeLevel: number): Bingo60TicketBook {
    const ticketBookSequence = getTicketBookSequence();
    return ticketBookSequence[prizeLevel - 1];
  }

  protected trackPrizeWinners(winnerSets: IGameStageWinnerSet[]): void {
    const gameIdentity = this.game.gameIdentity;
    winnerSets.forEach((winnerSet) => {
      const prizeName = winnerSet.prizeName;
      (winnerSet.ticketBooks || []).forEach((ticketBook) => {
        const trackedWinners = (ticketBook.winners || []).map<ITrackedWinner>((winner) => ({
          gameName: gameIdentity.name,
          clubName: gameIdentity.clubName,
          username: winner.username,
          prizeInPence: winner.prizeInPence,
          prizeName
        }));
        this.winnersTrackerService.trackWinners(...trackedWinners);
      });
    });
  }
}

export { WordSearchGameController, IWordSearchGameRepresentation };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/wordsearch/_domain/controller/game-controller.ts